/**
 * Chapter017.js — Plant & Equipment Pre-Start Checklist (OHSE 017)
 * Migrated from SWMS Platform Chapter017.tsx → JavaScript
 *
 * Props:
 *   data        {object}  equipment_prestart_check from swmsData
 *   onChange    {fn}      (newData) => void
 *   companyName {string}  from companyInfo.name
 */

import { useCallback, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';

const DEFAULT_PRESTART_ITEMS = [
  {
    item: 'Risk Assessment',
    description: 'A checklist should identify general hazards and associated risks relating to the use of the plant & equipment e.g. entanglement, crushing, striking, electrical or other. The checklist should then detail control measures to eliminate or minimise risk.',
    yes: false,
    no: false,
  },
  {
    item: 'Logbook',
    description: 'A current logbook recording daily safety Pre-start checks. These are subject to random inspection.',
    yes: false,
    no: false,
  },
  {
    item: 'Maintenance Reports',
    description: 'Proof of ongoing maintenance, i.e. maintenance records. The records should note the most recent inspection and who conducted that inspection. It may also describe any repair work carried out on the plant. Most importantly, there should be no outstanding items noted for repairs.',
    yes: false,
    no: false,
  },
  {
    item: "Operator's Manual",
    description: "An operator's manual relevant to the item of plant and which is to be kept with the plant.",
    yes: false,
    no: false,
  },
  {
    item: 'Operator Certification',
    description: "Copy of operator's certification or licence to operate the plant. Where no statutory certification is required, evidence of competence by the operator in the use of the plant.",
    yes: false,
    no: false,
  },
];

export default function Chapter017({ data = {}, onChange, companyName }) {
  const cn = companyName || '[Company Name]';

  const checklistItems = (data.checklist_items?.length === 5)
    ? data.checklist_items
    : DEFAULT_PRESTART_ITEMS;

  const plantInspected = useMemo(() => data.plant_inspected || [], [data.plant_inspected]);

  // ── Checklist handlers ──
  const updateChecklistItem = useCallback((idx, field, value) => {
    const items = checklistItems.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      // Mutual exclusion: yes/no
      if (field === 'yes' && value) updated.no = false;
      if (field === 'no' && value) updated.yes = false;
      return updated;
    });
    onChange({ ...data, checklist_items: items });
  }, [data, onChange, checklistItems]);

  // ── Provider field handlers ──
  const updateField = useCallback((field, value) => {
    onChange({ ...data, [field]: value });
  }, [data, onChange]);

  // ── Plant Inspected handlers ──
  const addPlantInspected = useCallback(() => {
    onChange({ ...data, plant_inspected: [...plantInspected, { plant_name: '', plant_id: '', company: '' }] });
  }, [data, onChange, plantInspected]);

  const removePlantInspected = useCallback((idx) => {
    onChange({ ...data, plant_inspected: plantInspected.filter((_, i) => i !== idx) });
  }, [data, onChange, plantInspected]);

  const updatePlantInspected = useCallback((idx, field, value) => {
    const entries = [...plantInspected];
    entries[idx] = { ...entries[idx], [field]: value };
    onChange({ ...data, plant_inspected: entries });
  }, [data, onChange, plantInspected]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 017</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">
          Plant &amp; Equipment Pre-Start Checklist
        </h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed">
        <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
        completes the following checklist prior to initial plant operation at the workplace.
      </div>

      {/* Pre-Start Checklist Table */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">Pre-Start Checklist</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-[#1a1a2e]">
                <th className="text-left py-2.5 px-3 font-semibold text-[#1a1a2e] w-[160px]">Item</th>
                <th className="text-left py-2.5 px-3 font-semibold text-[#1a1a2e]">Description</th>
                <th className="text-center py-2.5 px-3 font-semibold text-[#1a1a2e] w-[140px]">Check</th>
              </tr>
            </thead>
            <tbody>
              {checklistItems.map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-[#faf9f6]' : 'bg-white'}>
                  <td className="py-3 px-3 align-top font-semibold text-[#1a1a2e] text-xs">{item.item}</td>
                  <td className="py-3 px-3 align-top text-xs text-[#1a1a2e]/80 leading-relaxed">{item.description}</td>
                  <td className="py-3 px-3 align-middle">
                    <div className="flex items-center justify-center gap-4">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={item.yes}
                          onChange={e => updateChecklistItem(idx, 'yes', e.target.checked)}
                          className="w-4 h-4 rounded border-[#e0dbd0] cursor-pointer" />
                        <span className="text-xs font-semibold text-[#1a1a2e]">Yes</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={item.no}
                          onChange={e => updateChecklistItem(idx, 'no', e.target.checked)}
                          className="w-4 h-4 rounded border-[#e0dbd0] cursor-pointer" />
                        <span className="text-xs font-semibold text-[#1a1a2e]">No</span>
                      </label>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Plant Provider */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">Plant Provider</h3>
        </div>
        <div className="p-4 grid grid-cols-3 gap-4">
          <Field label="Signature (type name)">
            <input type="text" value={data.plant_provider_signature || ''} placeholder="Signature"
              onChange={e => updateField('plant_provider_signature', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Name">
            <input type="text" value={data.plant_provider_name || ''} placeholder="Full name"
              onChange={e => updateField('plant_provider_name', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Date">
            <input type="date" value={data.plant_provider_date || ''}
              onChange={e => updateField('plant_provider_date', e.target.value)} className={inputCls} />
          </Field>
        </div>
      </div>

      {/* Plant Inspected */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">Plant Inspected</h3>
          <p className="text-xs text-gray-500 mt-0.5">Record of plant inspected on site</p>
        </div>
        <div className="p-4 space-y-3">
          {plantInspected.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No plant inspected records.</p>
          )}
          {plantInspected.map((entry, idx) => (
            <div key={idx} className="relative group grid grid-cols-3 gap-4 p-3 border border-[#e0dbd0] rounded-lg bg-[#faf9f6] hover:shadow-sm transition-shadow">
              <button type="button" onClick={() => removePlantInspected(idx)}
                className="absolute top-2 right-2 p-1 text-red-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <Field label="Plant Name">
                <input type="text" value={entry.plant_name} placeholder="e.g. Excavator, Scissor Lift"
                  onChange={e => updatePlantInspected(idx, 'plant_name', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Plant ID / Serial No.">
                <input type="text" value={entry.plant_id} placeholder="Serial or registration number"
                  onChange={e => updatePlantInspected(idx, 'plant_id', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Company (Plant Provider)">
                <input type="text" value={entry.company} placeholder="e.g. Coherent Fitout Pty Ltd"
                  onChange={e => updatePlantInspected(idx, 'company', e.target.value)} className={inputCls} />
              </Field>
            </div>
          ))}
          <div className="flex justify-center pt-2">
            <button type="button" onClick={addPlantInspected}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
              <Plus className="w-4 h-4" /> Add Plant Inspected
            </button>
          </div>
        </div>
      </div>

      {/* Inspection Verified By */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">Inspection Verified By</h3>
        </div>
        <div className="p-4 grid grid-cols-3 gap-4">
          <Field label="Signature (type name)">
            <input type="text" value={data.verified_by_signature || ''} placeholder="Signature"
              onChange={e => updateField('verified_by_signature', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Name">
            <input type="text" value={data.verified_by_name || ''} placeholder="Full name"
              onChange={e => updateField('verified_by_name', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Date">
            <input type="date" value={data.verified_by_date || ''}
              onChange={e => updateField('verified_by_date', e.target.value)} className={inputCls} />
          </Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
